<template>
  <div :class="['relative', 'no-localize-translate', dropdownEnabled ? 'ver-datepicker-with-dropdown' : 'ver-datepicker']">
    <VueDatepickerUi
      :ref="`ver-datepicker-${uid}`"
      v-model="selectedDate"
      lang="en"
      input-class="ver-datepicker-input"
      range
      range-seperator="-"
      :disabled-start-date="disabledStartDate"
      :disabled-end-date="disabledEndDate"
      @input="keepPickerAlive"
      @change="$emit('change', $event)"
    />
    <button
      v-if="dropdownEnabled"
      type="button"
      class="ver-daterange-picker-dropdown absolute flex items-center justify-center left-[247px] bottom-[10px] outline-none"
    >
      <p class="text-[#D8DCDE] p-1 ver-daterange-picker-dropdown">
        |
      </p>
      <DropdownIcon :custom-style="`ver-daterange-picker-dropdown size-4 ${pickerIsShown ? 'rotate-180' : ''}`" />
    </button>

    <section
      v-if="pickerIsShown"
      :class="{ '-left-[200px]': extraRangesPosition === 'left', 'left-[651.94px]': extraRangesPosition === 'right' }"
      class="absolute transition-all top-[61px] w-[200px] h-min shadow-lg bg-white z-[99999]"
    >
      <div class="w-full px-2 py-4 text-gray-400">
        <h3 class="font-normal">
          Select a date range
        </h3>
        <div class="flex flex-col pb-2 mt-4 text-sm font-normal border-b">
          <button
            v-for="({ name }, rangeIndex) in ranges"
            :key="name"
            type="button"
            :class="{ 'bg-[#218579]/5': rangeIndex === activeRange }"
            class="w-full capitalize outline-none border-none text-left px-2.5 py-1 hover:rounded-sm hover:bg-[#218579]/5"
            @click="updateRangePicker(rangeIndex)"
          >
            {{ name }}
          </button>
        </div>
        <div class="flex items-center justify-between mt-2 text-xs">
          <GhostButton
            classes="px-6 py-1"
            @click="cancelPicker"
          >
            Cancel
          </GhostButton>
          <PrimaryButton
            classes="px-6 py-1"
            :disabled="cantApplyDates"
            @click="applySelection"
          >
            Apply
          </PrimaryButton>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import 'vue-datepicker-ui/lib/vuedatepickerui.css'
import VueDatepickerUi from 'vue-datepicker-ui'

export default {
  name: 'DateRangePicker',

  components: {
    VueDatepickerUi,
  },

  props: {
    dateSelected: {
      type: Array,
      default: () => [],
    },
    dropdownEnabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['change', 'datesSelected'],

  data() {
    return {
      uid: null,
      selectedDate: this.dateSelected,
      pickerIsShown: false,
      ranges: [],
      extraRangesPosition: null,
      activeRange: null,
    }
  },

  computed: {
    cantApplyDates() {
      return this.selectedDate.length !== 2 || this.selectedDate.some(date => !date || date === undefined)
    },
    disableAll() {
      return {
        from: new Date(0),
        to: new Date(new Date().getTime() + 365000 * 24 * 60 * 60 * 1000),
      }
    },
    disabledStartDate() {
      if (this.activeRange !== this.ranges.length - 1) {
        return this.disableAll
      }
      return { from: null, to: null }
    },
    disabledEndDate() {
      if (this.activeRange !== this.ranges.length - 1) {
        return this.disableAll
      }

      return {
        // Dates after the disabledDatesAfter or by default dates after 1000 years from now (365000) days
        from: new Date(new Date().getTime() + 365000 * 24 * 60 * 60 * 1000),
      }
    },
  },

  mounted() {
    this.uid = this.$.uid
    // Toggle closing the extra-ranges container for click aways and input clicks
    document.addEventListener('click', this.handleClicks, true)
    // Adjust the position of the extra-ranges section on the picker
    this.repositionRangesContainer()
    // TODO: Debounce this listener
    window.addEventListener('resize', this.repositionRangesContainer, true)

    this.fetchRanges()
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClicks, true)
    window.removeEventListener('resize', this.repositionRangesContainer, true)
  },

  methods: {
    repositionRangesContainer() {
      nextTick(() => {
        const { left: leftWindowOffset } = this.$el.getBoundingClientRect()
        /*
         * 1. The max width of the aside navigation menu = 256px
         * 2. The max left padding inside the dashboard layout = 32px
         * 3. The extra-ranges container width = 200px
         *
         * TOTAL = 488px
         */
        this.extraRangesPosition = leftWindowOffset > 488 ? 'left' : 'right'
      })
    },
    handleClicks(event) {
      const isThis = this.$el.contains(event.target)
      const isTargetClassMatched = ['ver-datepicker-input', 'ver-daterange-picker-dropdown'].some(className => event.target.classList.contains(className))

      if (isTargetClassMatched) {
        this.pickerIsShown = !this.pickerIsShown
        if (event.target.classList.contains('ver-daterange-picker-dropdown') && this.pickerIsShown) {
          this.keepPickerAlive()
        }
      }
      else {
        this.pickerIsShown = isThis
        if (isThis) {
          this.keepPickerAlive()
        }
      }
    },
    keepPickerAlive() {
      setTimeout(() => {
        if (!this.$refs[`ver-datepicker-${this.uid}`]?.isShowPicker && this.pickerIsShown) {
          // Force the 3rd party component to still remain open, we close it when we want
          this.$refs[`ver-datepicker-${this.uid}`].isShowPicker = true
        }
      }, 1)
    },
    fetchRanges() {
      const cid = localStorage.getItem('cid')
      this.$apis.metrics.reportingPeriodRanges(cid).then(({ data: ranges }) => {
        this.ranges = ranges
        this.ranges.push({ name: 'custom', start_date: null, end_date: null })
        this.activeRange = this.ranges.length - 1
      })
    },
    updateRangePicker(index) {
      this.activeRange = index
      const { start_date, end_date } = this.ranges[index]
      this.selectedDate = start_date !== null ? [new Date(start_date), new Date(end_date)] : []
    },
    cancelPicker() {
      this.activeRange = this.ranges.length - 1
      this.selectedDate = []
      this.$emit('datesSelected', { reporting_period: null, start_date: null, end_date: null })
      this.hidePicker()
    },
    applySelection() {
      const start_date = this.$moment(this.selectedDate[0]).format('YYYY-MM-DD')
      const end_date = this.$moment(this.selectedDate[1]).format('YYYY-MM-DD')

      this.$emit('datesSelected', { reporting_period: this.ranges[this.activeRange].name, start_date, end_date })
      this.hidePicker()
    },
    hidePicker() {
      this.$refs[`ver-datepicker-${this.uid}`].isShowPicker = false
      this.pickerIsShown = false
    },
  },
}
</script>

<style>
.ver-datepicker>.v-calendar .input-field input {
  border-radius: 0.125rem !important;
  height: 48px !important;
}

.ver-datepicker>.v-calendar .input-field input:focus {
  border-color: transparent !important;
  outline: none !important;
  height: 46px !important;
  box-shadow: 0 0 0 2px rgb(33 133 121 / 1) !important;
}

.ver-datepicker>* {
  --v-calendar-active-bg-color: #218579 !important;
  --v-calendar-triangle-color: #ffffff !important;
}

.ver-datepicker-with-dropdown .v-calendar .input-field svg.datepicker {
  top: 31px;
}

.ver-datepicker-with-dropdown .v-calendar .input-field input {
  padding-top: 18px;
  margin-top: 2px;
  height: 48px !important;
}

.ver-datepicker-with-dropdown .v-calendar .input-field input:focus {
  box-shadow: 0 0 0 1px rgb(33 133 121 / 1) !important;
}
</style>
